import React, { useEffect, useState } from "react";
import clsx from "clsx";
import { useTranslation } from "lib/i18n/use-translation";
import styles from "./OriMessageList.module.scss";

// One breath per swap. Matches the `breath-cycle` animation duration, so the
// word changes at the trough of its own fade rather than mid-fade.
const BREATH_INTERVAL_MS = 2500;

// Standalone moments of delight — each must read complete on its own, since
// most waits only last long enough to show one.
export const PLACEHOLDER_STATUS_KEYS = [
  "inhalingAndExhaling",
  "lettingItLand",
  "pausingAndProcessing",
  "connectingTheDots",
  "smellingTheRoses",
] as const;

// Fisher–Yates. Each wait draws its own order so the phrases never read as a
// fixed script across turns.
const shuffledPhraseOrder = () => {
  const order = [...PLACEHOLDER_STATUS_KEYS];
  for (let i = order.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [order[i], order[j]] = [order[j], order[i]];
  }
  return order;
};

interface OriTypingIndicatorProps {
  // Transient activity/watchdog line ("Looking at your Index…"); replaces the
  // dots in the same slot. Never part of the transcript.
  statusText?: string | null;
  // With `ori_worded_loading`, a turn with no specific status cycles the
  // placeholder phrases instead of showing the three dots.
  wordedDefault?: boolean;
}

const OriTypingIndicator: React.FC<OriTypingIndicatorProps> = ({
  statusText = null,
  wordedDefault = false,
}) => {
  const { t } = useTranslation(["ori"]);
  const breathing = wordedDefault && !statusText;
  const [phraseOrder, setPhraseOrder] = useState(shuffledPhraseOrder);
  const [phraseIndex, setPhraseIndex] = useState(0);

  useEffect(() => {
    if (!breathing) return undefined;
    setPhraseOrder(shuffledPhraseOrder());
    setPhraseIndex(0);
    const id = setInterval(
      () =>
        setPhraseIndex(
          (previous) => (previous + 1) % PLACEHOLDER_STATUS_KEYS.length
        ),
      BREATH_INTERVAL_MS
    );
    return () => clearInterval(id);
  }, [breathing]);

  if (breathing) {
    const phraseKey = phraseOrder[phraseIndex];
    return (
      <div className={styles.assistantRow}>
        <div
          className={clsx(styles.activityStatus, styles.breathStatus)}
          data-testid="ori-activity-status"
          role="status"
          // The words cycle, so they carry no information a screen reader
          // needs twice a wait — announce the stable "Ori is typing" instead.
          aria-label={t("ori:chat.typingIndicator.a11y")}
        >
          {/* Remounting on each swap restarts the fade, keeping it in step
              with the interval without a second timer to drift against. */}
          <span key={phraseKey} className={styles.breathWord} aria-hidden>
            {t(`ori:chat.typingIndicator.placeholderStatus.${phraseKey}`)}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.assistantRow}>
      {statusText ? (
        <div
          className={styles.activityStatus}
          data-testid="ori-activity-status"
          role="status"
        >
          {statusText}
        </div>
      ) : (
        <div
          className={styles.typingBubble}
          data-testid="ori-typing-indicator"
          aria-label={t("ori:chat.typingIndicator.a11y")}
          role="status"
        >
          <span className={styles.typingDot} />
          <span className={styles.typingDot} />
          <span className={styles.typingDot} />
        </div>
      )}
    </div>
  );
};

export default OriTypingIndicator;
